/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@atlaskit/button/new';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import { Text, Box } from '@atlaskit/primitives/compiled';
import { dropTargetForExternal } from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import { getFiles } from '@atlaskit/pragmatic-drag-and-drop/external/file';
import { token } from '@atlaskit/tokens';
import { type RenderFn } from '@atlassian/forge-ui-types';

const dropzoneStyle = css({
	borderStyle: 'dashed',
	borderWidth: token('border.width'),
	borderColor: token('color.border.input'),
	borderRadius: token('radius.small'),
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	textAlign: 'center',
});

const inDropzoneStyle = css({
	borderColor: token('color.border.selected'),
	backgroundColor: token('color.background.accent.blue.subtlest'),
});
const notInDropzoneStyle = css({
	borderColor: token('color.border.input'),
});

type SerializedFile = {
	data: string;
	name: string;
	size: number;
	type: string;
};

export type FilePickerProps = {
	/**
	 * Description of the file picker to be used for file requirements
	 */
	description?: string;
	/**
	 * Label of the file picker
	 */
	label?: string;
	/**
	 * Callback function to be called when the files are changed
	 */
	onChange?: (files: SerializedFile[]) => void | Promise<void>;
	/**
	 * Test ID for testing
	 */
	testId?: string;
};

const serializeFiles = async (files: File[]) => {
	return Promise.all(
		files.map(async (file) => {
			const buffer = await file.arrayBuffer();
			return {
				name: file.name,
				type: file.type,
				size: file.size,
				data: Buffer.from(buffer).toString('base64'),
			};
		}),
	);
};

export const FilePicker = (props: Parameters<RenderFn>[0]) => {
	const { description, label, onChange, testId } = props.forgeDoc.props as FilePickerProps;
	const [inDropzone, setInDropzone] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);
	const dropRef = useRef<HTMLDivElement>(null);

	const onInputTriggerClick = useCallback(() => {
		inputRef.current?.click();
	}, []);

	useEffect(() => {
		const element = dropRef.current;
		if (!element) {
			return;
		}
		return dropTargetForExternal({
			element,
			onDragEnter: () => {
				setInDropzone(true);
			},
			onDragLeave: () => {
				setInDropzone(false);
			},
			onDrop: onChange
				? async ({ source }) => {
						const files = await getFiles({ source });
						const serializedFiles = await serializeFiles(files);
						await onChange(serializedFiles);
					}
				: undefined,
		});
	}, [onChange]);

	return (
		<Box testId={testId}>
			{label && (
				<Text weight="bold" size="small">
					{label}
				</Text>
			)}
			<input
				type="file"
				multiple
				onChange={
					onChange
						? async (e) => {
								const files = Array.from(e.target.files || []);
								const serializedFiles = await serializeFiles(files);
								await onChange(serializedFiles);
							}
						: undefined
				}
				hidden
				ref={inputRef}
			/>
			<div css={[dropzoneStyle, inDropzone ? inDropzoneStyle : notInDropzoneStyle]} ref={dropRef}>
				<Text color={inDropzone ? 'color.text.selected' : 'color.text.subtlest'}>
					<CloudArrowUpIcon
						label="file-picker-icon"
						color={inDropzone ? token('color.icon.selected') : token('color.icon')}
					/>
					{'  '}
					Drop files to attach or{'  '}
					<Button onClick={onInputTriggerClick}>Browse</Button>
				</Text>
			</div>
			{description && (
				<Text size="small" color="color.text.subtlest">
					{description}
				</Text>
			)}
		</Box>
	);
};
